import { z } from 'zod';
import type { AgentRunRequest } from '@/types/agent-run-contract.js';

export { type ConversationItem } from '@/agents/neo/conversation-items.js';
export type { AgenticTurnTraceEntry, AgenticTurnStopReason, AgenticTurnStreamEvent } from '@/agents/neo/agentic-turn.js';
export type { Speak2MeStreamEvent } from '@/agents/neo/speak2me-adapter.js';
export type { ChatWithToolsUsage } from '@/llm/types.js';

import type { ConversationItem } from '@/agents/neo/conversation-items.js';
import type { ParsedTemplateEntry, TemplateEntryDef, TemplateFieldType } from './template-fields.js';

export const WORK4ME_ENTITY_TYPES = ['TAG', 'TAG_GROUP', 'AGENT', 'SKILL', 'PROCESS'] as const;
export type Work4MeEntityType = typeof WORK4ME_ENTITY_TYPES[number];

/** Server-resolved from the instance's definition field; the model never composes one. */
export interface FieldInputPayload {
  instanceId: number;
  instanceCode: string;
  sectionCode: string;
  fieldKey: string;
  label: string;
  control: TemplateFieldType;
  options?: string[];
  allowOther?: boolean;
}

export interface FieldInputAction {
  label: string;
  kind: 'FIELD_INPUT';
  payload: FieldInputPayload;
}

/** Server-resolved from the section's entry definition and, for a correction, its parsed entry. */
export interface EntryFormPayload {
  instanceId: number;
  instanceCode: string;
  sectionCode: string;
  sectionTitle: string | null;
  def: TemplateEntryDef;
  prefill: ParsedTemplateEntry | null;
}

export interface EntryFormAction {
  label: string;
  kind: 'ENTRY_FORM';
  payload: EntryFormPayload;
}

export type SuggestedAction =
  | {
      label: string;
      kind: 'SEND_MESSAGE' | 'OPEN_TOOL' | 'OPEN_SETTINGS';
      payload?: Record<string, unknown>;
    }
  | FieldInputAction
  | EntryFormAction;

export type BudgetProfile = 'quality' | 'balanced' | 'economy';

// ONE definition of the turn's attachment envelope. It was previously declared three
// times — here, in RunSpeak2MeTurnInput, and again as a zod object in the route — and
// adding `observation` to the two TypeScript copies while missing the zod one is what
// silently 400'd every capture turn. The schema is the source; the type is inferred.
export const speak2MeAttachmentIdsSchema = z
  .object({
    photoIds: z.array(z.number().int().positive()).optional(),
    recordingIds: z.array(z.number().int().positive()).optional(),
    videoIds: z.array(z.number().int().positive()).optional(),
    fileIds: z.array(z.number().int().positive()).optional(),
    /** Present when the media came from one Observation Capture submit. */
    observation: z.object({ groupId: z.number().int().positive() }).strict().optional(),
  })
  .strict();

export type Speak2MeAttachmentIds = z.infer<typeof speak2MeAttachmentIdsSchema>;

export const VIEW_CONTEXT_LIMITS = {
  path: 500,
  code: 80,
  tab: 120,
  query: 200,
  selection: 25,
  address: 2000,
  subject: 500,
  unsavedBody: 6000,
  bodyPreview: 600,
  eventTitle: 300,
  eventTime: 40,
  eventLocation: 500,
  eventDescription: 2000,
  attendee: 320,
  attendees: 100,
  weekStartIso: 40,
  unsavedText: 6000,
} as const;

export type MessageComposerKind = 'compose' | 'reply';
export type MessageComposerMode = 'reply' | 'replyAll' | 'forward' | 'newFromContext';

/**
 * What the member has on the main page. Codes and ids are untrusted refs the server resolves
 * itself; `tab`, `query`, `unsaved`, `eventComposer` and `unsavedText` are member-typed text with
 * no server copy, rendered under an explicit client-reported label and capped.
 */
export interface Speak2MeViewContext {
  path: string;
  content?: { code: string; sectionCode?: string };
  explore?: {
    tab?: string;
    savedFilterId?: number;
    query?: string;
    selectedContentIds?: number[];
  };
  calendar?: { weekStartIso: string };
  messageComposer?: {
    kind: MessageComposerKind;
    draftContentId?: number;
    sourceContentId?: number;
    mode?: MessageComposerMode;
    unsaved?: { to: string; cc: string; bcc: string; subject: string; body: string };
  };
  eventComposer?: {
    title: string;
    startAt: string;
    endAt: string;
    isAllDay: boolean;
    location: string;
    description: string;
    attendees: string[];
  };
  documentEditor?: { sectionCode: string; unsavedText: string };
}

export interface Speak2MeTurnRequest {
  message: string;
  neoChatId?: number;
  modelOverride?: string | null;
  budgetProfile?: BudgetProfile;
  voiceConversation?: boolean;
  /** Binds an unbound VoiceSession to the chat this turn resolves, so its cost ledgers there. */
  voiceSessionId?: string;
  clientTimezone?: string;
  viewContext?: Speak2MeViewContext;
  attachmentIds?: Speak2MeAttachmentIds;
}

export interface Speak2MeResumeQuery {
  neoChatId?: number;
}

export interface Speak2MeEnsureChatRequest {
  neoChatId?: number;
}

export interface Speak2MeEnsureChatResponse {
  neoChatId: number;
  justCreated: boolean;
}

export interface Speak2MeResumeSnapshot {
  neoChatId: number;
  conversationItems: ConversationItem[];
  pendingExecutionRequest?: AgentRunRequest | null;
}

export interface Speak2MeResumeResponse {
  snapshot: Speak2MeResumeSnapshot | null;
}

export interface Speak2MeNeoChatConversationResponse {
  conversationItems: ConversationItem[];
}

export interface Speak2MeDictationTranscribeResponse {
  text: string;
}

export type VoiceSessionKind = 'CONVERSATION' | 'DICTATION';
export type SttProviderName = 'assemblyai' | 'deepgram';
export type SttAuth = 'query' | 'subprotocol';
export type TtsProviderName = 'azure' | 'polly' | 'deepgram';

export const MAX_TTS_TEXT_CHARS_BY_PROVIDER: Record<TtsProviderName, number> = {
  azure: 4000,
  polly: 3000,
  deepgram: 800,
};

export interface VoiceSessionMintRequest {
  neoChatId?: number;
  voiceSessionId?: string;
  kind?: VoiceSessionKind;
}

export interface VoiceSessionMintResponse {
  voiceSessionId: string;
  /** Null when the session is not yet bound to a chat — the member has said nothing. */
  neoChatId: number | null;
  sttProvider: SttProviderName;
  sttAuth: SttAuth;
  sttToken: string;
  sttTokenExpiresInSeconds: number;
  sttSocketUrl: string;
  ttsVoice: string;
  ttsMaxChars: number;
  /** Non-null only when this call created the session, so a reconnect never re-greets. */
  greeting: string | null;
}

export interface VoiceTtsRequest {
  voiceSessionId: string;
  text: string;
}

export interface VoiceTurnLatencyRequest {
  neoChatId: number;
  toolRounds: number;
  interimSentencesSpoken: number;
  eotToRequestMs: number;
  requestToFirstDeltaMs: number;
  firstDeltaToFirstSentenceMs: number;
  firstSentenceToTtsFirstByteMs: number;
  ttsFirstByteToPlayingMs: number;
  speechEndToFirstAudioMs: number;
}

export interface VoiceSessionSummary {
  voiceSessionId: string;
  sttSeconds: number;
  ttsCharacters: number;
  totalCostUsd: number | null;
}

